// /diaries/:id — single-diary view (W2 §6.3).
//
// W32 polish: align with AnniversaryDetailPage richness (5 sections).
//   Z1 Sticky glass header — back / title / share / like / mood (unchanged chrome)
//   Z2 DiaryHero — icon chip + meta row + author + privacy (W32 NEW)
//   Z3 DiaryBody — sanitized HTML render (was inside the old Card; now its own)
//   Z4 DiaryPhotoGallery — full-size grid + lightbox (W32 NEW; was tiny inline
//                          grid that opened a new tab)
//   Z5 DiarySiblingsNav — prev/next diary cards (W32 NEW; mirrors Anniversary)
//   Z6 Author actions — edit / delete (author-only, was inside old Card)
//
// W32 polish also wraps the body in PageContainer so the global gutter
// (`px-s-4 md:px-s-6` + `max-w-screen-md mx-auto`) is honored — previously
// the body was edge-to-edge on mobile because the parent div had no
// horizontal padding.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit, Share2, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PageHero } from "@/components/primitives/PageHero";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { UndoBar } from "@/components/patterns/UndoBar";
import { HoldToConfirmButton } from "@/components/system/HoldToConfirmButton";
import { useUndoBar } from "@/hooks/useUndoBar";
import { ApiError } from "@/lib/api/pair";
import {
  listDiaries,
  readDiary,
  restoreDiary,
  softDeleteDiary,
} from "@/lib/api/diary";
import { diaryErrorMessage } from "@/lib/i18n/diary";
import { emojiForCode } from "@/lib/diary/mood";
import { useAuth } from "@/lib/auth/AuthProvider";
import { queryKeys } from "@/lib/queries/keys";
import { useToast } from "@/components/ui/toast";
import { ShareSheet } from "@/components/system/ShareSheet";
import { formatRelative } from "@/lib/utils/date";
import { previewFromHtml } from "@/lib/utils/text";
import { getPublicUrl } from "@/lib/config";
import { DiaryLikeButton } from "@/components/diary/DiaryLikeButton";
import { PageContainer, SectionStack } from "@/components/layout";
import { DiaryHero } from "./diaries/components/DiaryHero";
import { DiaryPhotoGallery } from "./diaries/components/DiaryPhotoGallery";
import { DiarySiblingsNav } from "./diaries/components/DiarySiblingsNav";
import { LinkedWishPanel } from "@/components/diary/LinkedWishPanel";

export function DiaryDetailPage() {
  const params = useParams<{ id: string }>();
  const diaryId = params.id ?? "";
  const nav = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { push: pushToast } = useToast();

  const diaryQuery = useQuery({
    queryKey: queryKeys.diary.detail(diaryId),
    queryFn: () => readDiary(diaryId),
    enabled: diaryId.length > 0,
  });

  // Sibling list — only fetched after detail is ready.  Uses the same scope
  // as the user's current /diaries view (private + shared) so prev/next
  // matches what they'd see on the list page.  Cap at 50 to keep cheap.
  const siblingsQuery = useQuery({
    queryKey: queryKeys.diary.list(null, true),
    queryFn: () => listDiaries(null, 50, true),
    enabled: Boolean(diaryId) && Boolean(diaryQuery.data),
    staleTime: 30_000,
  });

  // W45.0 `ADR-0111` §P4: `editing` and `updateMut` are gone with the in-place
  // editor. Saving belongs to `/diaries/:id/edit`, which claims the bottom edge
  // for as long as it is mounted — a claim a route component can make and a
  // `useState` flag cannot.
  const [shareOpen, setShareOpen] = useState(false);

  // W39.1 R1 (ADR-0080): soft delete no longer confirms, and no longer
  // navigates. The undo bar is rendered by this page so the user's eyes never
  // leave the thing they just tapped. Navigating away first would destroy the
  // anchor the bar is positioned against.
  //
  // The detail query is left alone on purpose: invalidating it would blank the
  // body and take the bar's own context with it.
  const undo = useUndoBar<{ id: string }>({
    onExpire: () => nav("/diaries"),
  });

  const deleteMut = useMutation({
    mutationFn: () => softDeleteDiary(diaryId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.diary.list(null, true) });
      undo.begin({ id: diaryId });
    },
  });

  const restoreMut = useMutation({
    mutationFn: () => restoreDiary(diaryId),
    onSuccess: () => {
      undo.clear();
      void qc.invalidateQueries({ queryKey: queryKeys.diary.all });
      pushToast({ variant: "success", message: "日記已還原" });
    },
    onError: (err) => {
      // The bar stays on screen on a failed restore: the delete is real, and
      // hiding it would leave no route back short of the trash page.
      pushToast({
        variant: "danger",
        message: err instanceof ApiError ? diaryErrorMessage(err.code, err.message) : "還原失敗",
      });
    },
  });

  if (diaryQuery.isLoading) {
    return (
      <div data-testid="diary-detail-page">
        <LoadingState count={1} shape="card" />
      </div>
    );
  }
  if (diaryQuery.isError || !diaryQuery.data) {
    return (
      <div className="p-5">
        <ErrorState title="找不到日記" retryLabel="回到列表" onRetry={() => nav("/diaries")} />
      </div>
    );
  }

  const diary = diaryQuery.data;
  const isAuthor = user?.id === diary.author_id;
  const siblings = siblingsQuery.data?.items ?? [];

  return (
    // `pb-32` clears the floating PillNav (per W31 acceptance #2).  Sticky
    // header still floats above; the trailing edit / delete buttons inside
    // the diary body card live inside the content area so they get the
    // bottom safe area for free.
    <div data-testid="diary-detail-page" className="flex flex-col pb-32">
      {/* Sticky glass header — back / title / share / like / mood.  Edge-to-edge
          by design (matches AnniversaryDetail); the body below lives inside
          PageContainer so the global gutter is honored. */}
      {/* Per W35.5 spec §3.3 / §2.1: stick with the shared PageHero primitive
          (sticky glass chrome) so cross-module baseline applies. The back
          arrow lives in the eyebrow-leading slot; share/like/mood stack to
          the trailing slot. */}
      <PageHero
        // W45.0 `ADR-0115`: the eyebrow back link becomes the breadcrumb's first
        // segment. `back-to-list` is kept as the parent's testid so
        // `probe-back-affordances.mjs` and the e2e selectors still reach it —
        // the affordance moved, it did not disappear.
        crumbs={[
          { to: "/diaries", label: "日記", testId: "back-to-list" },
          { label: "詳情" },
        ]}
        title={diary.title || "日記"}
        subtitle={formatRelative(new Date(diary.created_at))}
        trailing={
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="tap-target inline-flex items-center justify-center w-11 h-11 text-ink-2 hover:text-ink-1 press"
              aria-label="分享日記"
              data-testid="diary-share"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <DiaryLikeButton diaryId={diaryId} className="mr-1" />
            {diary.mood ? (
              <span aria-label="mood" className="text-title-3 px-2">
                {emojiForCode(diary.mood)}
              </span>
            ) : null}
          </div>
        }
      />

      <PageContainer vertical="loose">
        {/*
          W45.0 `ADR-0111` §P4 — the editor is **gone from this screen**. It was
          a `useState` flag, and the flag is what made the defect: the bar's
          `pillNavHidden` branch depends on a *route-scoped* claim, so a
          state-gated editor could never claim it and the bar lifted itself by
          +72px onto the pill instead. Editing is now `/diaries/:id/edit`.

          This branch is therefore not a ternary any more, which also removes
          the second problem: `pb-32` on this container was clearance for a pill
          that the editing state was about to join with a second control row.
        */}
        <SectionStack density="loose">
          {/* Z2 DiaryHero — icon chip + meta + author + privacy (W32) */}
          <DiaryHero diary={diary} />

            {/* Z3 DiaryBody — sanitized Tiptap HTML render */}
            <Card>
              <div className="p-5 md:p-6 flex flex-col gap-4" data-testid="diary-body">
                {/* Tiptap output is HTML; render via dangerouslySetInnerHTML with the
                    same prose styles used by the editor (DiaryEditor.tsx). Content is
                    sanitized at write-time (only StarterKit marks are allowed). */}
                <div
                  className="prose prose-sm max-w-none text-ink-1 [&_h1]:text-xl [&_h1]:font-display [&_h2]:text-lg [&_h2]:font-display [&_h3]:text-base [&_h3]:font-display [&_p]:text-ink-1 [&_ul]:list-disc [&_ol]:list-decimal [&_blockquote]:border-l-2 [&_blockquote]:border-ink-3 [&_blockquote]:pl-3 [&_code]:text-sm [&_code]:bg-ink-1/5 [&_pre]:bg-ink-1/5 [&_pre]:p-2"
                  data-testid="diary-content-text"
                  dangerouslySetInnerHTML={{ __html: diary.content }}
                />
                {isAuthor && !undo.item ? (
                  <div className="flex gap-3 pt-4 border-t border-hairline">
                    <Button
                      variant="ghost"
                      // W45.0 `ADR-0111` §P4: editing is a route, not a flag.
                      onClick={() => nav(`/diaries/${diary.id}/edit`)}
                      data-testid="diary-edit"
                      leadingIcon={<Edit className="w-3.5 h-3.5" />}
                    >
                      編輯
                    </Button>
                    {/* W50.1 §1 — hold-to-confirm. ADR-0080 removed the confirm
                        dialog because its copy lied about a delete that has a
                        restore window; what replaced it was a bare tap. This
                        closes that gap without putting a modal back. */}
                    <HoldToConfirmButton
                      onConfirm={() => void deleteMut.mutateAsync()}
                      label="刪除日記"
                      icon={<Trash2 className="w-3.5 h-3.5" />}
                      busy={deleteMut.isPending}
                      testId="diary-delete"
                      className="min-h-11 rounded-md bg-partner-strong px-s-3 text-body-sm text-paper hover:bg-partner"
                    >
                      刪除
                    </HoldToConfirmButton>
                  </div>
                ) : null}
              </div>
            </Card>

            {/* Z4 DiaryPhotoGallery — full-size grid + lightbox (W32) */}
            {diary.images.length > 0 ? (
              <DiaryPhotoGallery images={diary.images} />
            ) : null}

            {/* W35.2: cross-module reference — show linked wish when this
                diary was created via POST /wishes/{id}/complete-with-diary.
                Per docs/spec/wishlist/W35.2-SUBRESOURCE-UI-POLISH.md §3.1. */}
            {diary.source_wish_id ? (
              <LinkedWishPanel wishId={diary.source_wish_id} />
            ) : null}

            {/* Z5 DiarySiblingsNav — prev/next diary cards (W32) */}
            <DiarySiblingsNav list={siblings} currentId={diary.id} />
        </SectionStack>
      </PageContainer>

      {/*
        R1 (ADR-0080). The confirm dialog that stood here said 「刪除後會移到
        垃圾桶, 7 天內可還原」 and then asked the user to confirm anyway — the
        same "confirming something reversible" move ADR-0078 called out. Gone.

        The bar is `fixed` and 108px up, both corrections from the first
        screenshot pass: at `bottom-0` inside the Card it overlapped the body
        copy (two sets of text read through each other), and anchoring it to
        the card meant it scrolled away exactly when the user scrolled to check
        what they had just deleted. 108px clears the W38.1 three-island pill
        (60px + `bottom-4` + safe-area inset) — the second screenshot had the
        pill sitting on top of 「已刪除」.
      */}
      {undo.item ? (
        <div
          data-testid="diary-undo-slot"
          className="pointer-events-none fixed inset-x-0 bottom-[108px] z-40 flex justify-center px-4"
        >
          <div className="pointer-events-auto relative w-full max-w-md">
            <UndoBar
              open
              message="日記已刪除"
              onUndo={() => void restoreMut.mutateAsync()}
              className="inset-x-0"
            />
          </div>
        </div>
      ) : null}
      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={`分享「${diary.title || "日記"}」`}
        text={`${previewFromHtml(diary.content, 80)} · ${diary.date}${
          diary.mood ? ` · ${emojiForCode(diary.mood)}` : ""
        }`}
        url={`${getPublicUrl()}/diaries/${diary.id}`}
        testId="diary-share-sheet"
      />
    </div>
  );
}
